import { withContextRequestHeader } from '@/auth/contextRequestHeader'
import { ensureOidcUserFreshForApi } from '@/auth/ensureOidcUserFreshForApi'
import { env } from '@/config/env'

export type IsGrantedProps = {
  userId?: string
  attribute: string
  resource?: string
  accessToken?: string
}

const inflightByKey = new Map<string, Promise<boolean>>()
const recentDecisionByKey = new Map<
  string,
  {
    value: boolean
    expiresAt: number
  }
>()
const RECENT_GRANT_DECISION_TTL_MS = 10_000

function cacheKeyForIsGranted(
  props: IsGrantedProps,
  token: string | undefined
): string {
  const userPart = props.userId ?? token?.slice(-24) ?? 'me'
  const resourcePart = props.resource ?? ''
  const contextPart =
    typeof window === 'undefined'
      ? ''
      : (window.localStorage.getItem('contextIri') ?? '')
  return `${userPart}:${contextPart}:${props.attribute}:${resourcePart}`
}

async function fetchIsGranted(
  props: IsGrantedProps,
  token: string | undefined
): Promise<boolean> {
  const { userId, attribute, resource } = props
  let url = `${env.API_URL}/isGranted${userId ? `/${userId}` : ''}?attribute=${attribute}`
  if (resource) {
    url += `&resource=${encodeURIComponent(resource)}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: withContextRequestHeader({
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      }),
    })

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`)
    }

    const data = await response.json()

    return data.isGranted
  } catch {
    return false
  }
}

/** Deduplicates concurrent identical checks (Strict Mode, unstable route remounts). */
export const isGranted = async (props: IsGrantedProps): Promise<boolean> => {
  const token =
    props.accessToken ??
    (
      await ensureOidcUserFreshForApi({
        source: 'isGranted',
        operationName: null,
      })
    )?.access_token

  const key = cacheKeyForIsGranted(props, token)
  const recent = recentDecisionByKey.get(key)
  if (recent && recent.expiresAt > Date.now()) {
    return recent.value
  }
  const existing = inflightByKey.get(key)
  if (existing) {
    return existing
  }

  const promise = fetchIsGranted(props, token)
    .then((decision) => {
      recentDecisionByKey.set(key, {
        value: decision,
        expiresAt: Date.now() + RECENT_GRANT_DECISION_TTL_MS,
      })
      return decision
    })
    .finally(() => {
      inflightByKey.delete(key)
    })
  inflightByKey.set(key, promise)
  return promise
}
